import maplibregl, {
    Map as MaplibreMap,
    MapGeoJSONFeature,
    PointLike,
    IControl,
    ControlPosition as MaplibreControlPosition,
} from "maplibre-gl";
import type { MapEngine } from "./MapEngine";
import type {
    CameraState,
    ControlPosition,
    EngineDebugSettings,
    EngineEventHandler,
    EngineEventType,
    FlyToTarget,
    MapControl,
    QueriedFeature,
    ScreenPoint,
    StyleJson,
} from "./types";

/** Default pixel tolerance for point feature queries (a 2*N px box). */
const DEFAULT_QUERY_TOLERANCE_PX = 3;

/**
 * MapLibre-backed implementation of {@link MapEngine}.
 *
 * Wraps a single `maplibregl.Map` and translates between renderer-agnostic
 * engine types and MapLibre's API. Constructed by `createEngine` — callers
 * depend on the `MapEngine` interface, not this class.
 */
export class MapLibreEngine implements MapEngine {
    private readonly map: MaplibreMap;

    constructor(map: MaplibreMap) {
        this.map = map;
    }

    // -- Lifecycle --

    isLoaded(): boolean {
        return this.map.loaded();
    }

    waitForLoad(): Promise<void> {
        if (this.map.loaded()) {
            return Promise.resolve();
        }
        return new Promise<void>((resolve, reject) => {
            const onLoad = () => {
                this.map.off("error", onError);
                resolve();
            };
            const onError = (e: { error?: Error }) => {
                // Style-load failures surface as error events before load.
                if (!this.map.loaded()) {
                    this.map.off("load", onLoad);
                    reject(e.error ?? new Error("Map failed to load"));
                }
            };
            this.map.once("load", onLoad);
            this.map.on("error", onError);
        });
    }

    destroy(): void {
        this.map.remove();
    }

    getMap(): unknown {
        return this.map;
    }

    // -- Style --

    setStyle(style: StyleJson): void {
        this.map.setStyle(style as maplibregl.StyleSpecification);
    }

    getStyle(): StyleJson | null {
        if (!this.map.isStyleLoaded()) {
            return null;
        }
        return this.map.getStyle() as unknown as StyleJson;
    }

    // -- Camera --

    getCamera(): CameraState {
        const center = this.map.getCenter();
        return {
            lng: center.lng,
            lat: center.lat,
            zoom: this.map.getZoom(),
            pitch: this.map.getPitch(),
            bearing: this.map.getBearing(),
        };
    }

    flyTo(target: FlyToTarget): void {
        this.map.flyTo({
            center: [target.lng, target.lat],
            zoom: target.zoom,
            pitch: target.pitch,
            bearing: target.bearing,
            animate: target.animate ?? true,
        });
    }

    resize(): void {
        this.map.resize();
    }

    // -- Debug settings --

    setDebugSettings(settings: EngineDebugSettings): void {
        if (this.map.showTileBoundaries !== settings.showTileBoundaries) {
            this.map.showTileBoundaries = settings.showTileBoundaries;
        }
        if (this.map.showCollisionBoxes !== settings.showCollisionBoxes) {
            this.map.showCollisionBoxes = settings.showCollisionBoxes;
        }
        // showOverdrawInspector is present on the runtime map but not always typed.
        const m = this.map as unknown as { showOverdrawInspector: boolean };
        if (m.showOverdrawInspector !== settings.showOverdrawInspector) {
            m.showOverdrawInspector = settings.showOverdrawInspector;
        }
    }

    // -- Sources & layers (overlays) --

    setGeoJsonSource(id: string, data: unknown): boolean {
        if (!this.map.isStyleLoaded()) {
            return false;
        }
        const existing = this.map.getSource(id) as maplibregl.GeoJSONSource | undefined;
        if (existing) {
            existing.setData(data as any);
        } else {
            this.map.addSource(id, { type: "geojson", data: data as any });
        }
        return true;
    }

    addOverlayLayer(layer: Record<string, any>): void {
        if (!this.map.isStyleLoaded() || this.map.getLayer(layer.id)) {
            return;
        }
        this.map.addLayer(layer as any);
    }

    removeOverlay(sourceId: string, layerIds: string[]): void {
        for (const id of layerIds) {
            if (this.map.getLayer(id)) {
                this.map.removeLayer(id);
            }
        }
        if (this.map.getSource(sourceId)) {
            this.map.removeSource(sourceId);
        }
    }

    // -- Layer visibility --

    setLayerVisibility(layerId: string, visible: boolean): void {
        if (!this.map.isStyleLoaded() || !this.map.getLayer(layerId)) {
            return;
        }
        this.map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
    }

    getLayerIds(): string[] {
        if (!this.map.isStyleLoaded()) {
            return [];
        }
        const style = this.map.getStyle();
        return (style.layers ?? []).map((layer) => layer.id);
    }

    // -- Feature query --

    queryFeaturesAtPoint(
        point: ScreenPoint,
        tolerancePx: number = DEFAULT_QUERY_TOLERANCE_PX
    ): QueriedFeature[] {
        if (!this.map.isStyleLoaded()) {
            return [];
        }
        const box: [PointLike, PointLike] = [
            [point.x - tolerancePx, point.y + tolerancePx],
            [point.x + tolerancePx, point.y - tolerancePx],
        ];
        const features = this.map.queryRenderedFeatures(box);
        return features.map((f) => this.toQueriedFeature(f));
    }

    private toQueriedFeature(f: MapGeoJSONFeature): QueriedFeature {
        return {
            id: f.id,
            geometryType: f.geometry?.type,
            properties: f.properties ?? {},
            layerId: f.layer?.id,
            source: f.source,
            sourceLayer: f.sourceLayer,
        };
    }

    showPopup(lngLat: { lng: number; lat: number }, html: string, maxWidthPx = 350): void {
        new maplibregl.Popup({ maxWidth: `${maxWidthPx}px` })
            .setLngLat([lngLat.lng, lngLat.lat])
            .setHTML(html)
            .addTo(this.map);
    }

    // -- Controls --

    addControl(control: MapControl, position: ControlPosition): void {
        // Adapt our renderer-agnostic MapControl to MapLibre's IControl.
        const adapter: IControl = {
            onAdd: () => control.onAdd(),
            onRemove: () => control.onRemove(),
        };
        this.map.addControl(adapter, position as MaplibreControlPosition);
    }

    addNavigationControl(position: ControlPosition): void {
        this.map.addControl(
            new maplibregl.NavigationControl(),
            position as MaplibreControlPosition
        );
    }

    addFullscreenControl(position: ControlPosition): void {
        this.map.addControl(
            new maplibregl.FullscreenControl(),
            position as MaplibreControlPosition
        );
    }

    // -- Events --

    on<E extends EngineEventType>(event: E, handler: EngineEventHandler<E>): () => void {
        const wrapped = this.wrapHandler(event, handler);
        this.handlers.set(this.handlerKey(event, handler), wrapped);
        this.map.on(event as any, wrapped as any);
        return () => this.off(event, handler);
    }

    off<E extends EngineEventType>(event: E, handler: EngineEventHandler<E>): void {
        const key = this.handlerKey(event, handler);
        const wrapped = this.handlers.get(key);
        if (wrapped) {
            this.map.off(event as any, wrapped as any);
            this.handlers.delete(key);
        }
    }

    /**
     * Tracks wrapped listeners keyed by (event, handler) so `off` removes the
     * exact listener `on` added — and so the same handler can be registered for
     * multiple events without one registration clobbering another.
     */
    private readonly handlers = new Map<string, (e: any) => void>();
    private readonly handlerIds = new WeakMap<object, number>();
    private nextHandlerId = 1;

    /** Builds a stable composite key from an event name and a handler identity. */
    private handlerKey(event: EngineEventType, handler: Function): string {
        let id = this.handlerIds.get(handler);
        if (id === undefined) {
            id = this.nextHandlerId++;
            this.handlerIds.set(handler, id);
        }
        return `${event} ${id}`;
    }

    /**
     * Translates MapLibre's raw event into the engine's payload. Click events
     * get the {lngLat, point} shape; other events carry no payload.
     */
    private wrapHandler<E extends EngineEventType>(
        event: E,
        handler: EngineEventHandler<E>
    ): (e: any) => void {
        if (event === "click") {
            const clickHandler = handler as EngineEventHandler<"click">;
            return (e: any) => {
                clickHandler({
                    lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
                    point: { x: e.point.x, y: e.point.y },
                });
            };
        }
        const voidHandler = handler as () => void;
        return () => voidHandler();
    }
}
